import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/apiClient";
import { TranscriptQueryRequestSchema } from "@/api/generated";
import {
  confirmRecordingConsentApiV1TranscriptionConsentPost,
  queryTranscriptApiV1TranscriptionQueryTranscriptPost,
  transcribeAndSummarizeAudioApiV1TranscriptionSummarizePost,
} from "@/api/generated/sdk.gen";
import { CLIENT_KEYS } from "@/constants/clientKeys";

/**
 * Hook to confirm Assessment Scribe recording consent for a case (SCRIBE-02).
 * Must succeed before a live recording session is started — see
 * AudioRecorder.tsx's `handleStart`.
 */
export const useConfirmRecordingConsent = () => {
  return useMutation({
    mutationFn: async (caseNumber: string) => {
      const response = await confirmRecordingConsentApiV1TranscriptionConsentPost({
        client: apiClient,
        body: { case_number: caseNumber },
      });

      if ("error" in response && response.error) {
        throw new Error(
          `Failed to confirm recording consent: ${
            (response.error as { detail?: string }).detail ||
            JSON.stringify(response.error)
          }`
        );
      }

      return response.data;
    },
  });
};

/**
 * Hook for querying transcripts
 * @returns Mutation for querying transcripts
 */
export const useQueryTranscript = () => {
  return useMutation({
    mutationFn: async (data: TranscriptQueryRequestSchema) => {
      const response = await queryTranscriptApiV1TranscriptionQueryTranscriptPost({
        client: apiClient,
        body: data,
      });

      // Check for errors in the response
      if ("error" in response && response.error) {
        throw new Error(
          `Failed to query transcript: ${
            response.error.detail || JSON.stringify(response.error)
          }`
        );
      }

      return response.data;
    },
  });
};

/**
 * Hook to start transcription + summary task
 */
export const useStartTranscriptionSummary = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: FormData) => {
      const response =
        await transcribeAndSummarizeAudioApiV1TranscriptionSummarizePost({
          body: formData as any,
          client: apiClient,
        });

      if ("error" in response && response.error) {
        throw new Error(JSON.stringify(response.error));
      }

      return response.data;
    },
    onSuccess: (_data) => {
      // Invalidate tasks so any task list UI refreshes
      queryClient.invalidateQueries({ queryKey: [CLIENT_KEYS.TASKS] });

      // Also refresh assessments cache if needed
      queryClient.invalidateQueries({ queryKey: [CLIENT_KEYS.ASSESSMENTS] });
    },
    onError: (err) => {
      console.error("Error starting transcription summary:", err);
    },
  });
};

export default useStartTranscriptionSummary;
